import React, { useMemo, useState } from "react";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@mui/material";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { ChevronDown, Filter, MoreVertical, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import TableComponent from "../../../shared/components/table";
import { ARROW_LEFT } from "../../../assets";

const columnHelper = createColumnHelper<any>();

const baseColumns = [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  columnHelper.accessor("date_created", {
    header: "Date created",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <span className="text-[#464255] font-medium">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("phone", {
    header: "Phone number",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("total_cases", {
    header: "Total cases",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
];

const Users = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("private_individuals");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Status");
  const [enabledFilter, setEnabledFilter] = useState("Enabled");

  const tabs = [
    { id: "private_individuals", label: "Private  Individual (20)" },
    { id: "lawyers", label: "Lawyers" },
    { id: "registrar1_payment", label: "Registrar 1 payment" },
    { id: "registrar1_assessment", label: "Registrar 1 assessment" },
    { id: "registrar2", label: "Registrar 2 (30)" },
    { id: "panels", label: "Panel (4)" },
    { id: "president", label: "President" },
    { id: "baliff_it", label: "Baliff / IT staff" },
    { id: "police", label: "Police" },
  ];

  const mockData = [
    {
      sn: false,
      date_created: "31-05-2024",
      name: "Michael Ojo",
      email: "michaelojo@gmail.com",
      phone: "+234 809 755 434",
      total_cases: 8,
      scn_number: "SCN/12345",
      no_of_cases: 12,
      judicial_division: "Agbani",
      court_number: "Court 3",
    },
    {
      sn: false,
      date_created: "31-05-2024",
      name: "Michael Ojo",
      email: "michaelojo@gmail.com",
      phone: "+234 809 755 434",
      total_cases: 8,
      scn_number: "SCN/67890",
      no_of_cases: 4,
      judicial_division: "Enugu",
      court_number: "Court 2",
    },
    {
      sn: false,
      date_created: "31-05-2024",
      name: "Michael Ojo",
      email: "michaelojo@gmail.com",
      phone: "+234 809 755 434",
      total_cases: 8,
      scn_number: "SCN/99887",
      no_of_cases: 20,
      judicial_division: "Agbani",
      court_number: "Court 1",
    },
  ];

  const columns = useMemo(() => {
    const extraColumns = [];

    if (activeTab === "lawyers") {
      extraColumns.push(
        columnHelper.accessor("scn_number", {
          header: "SCN Number",
          cell: (info) => (
            <span className="text-[#464255]">{info.getValue()}</span>
          ),
        }),
        columnHelper.accessor("no_of_cases", {
          header: "No of Cases",
          cell: (info) => (
            <span className="text-[#464255]">{info.getValue()}</span>
          ),
        }),
      );
    }

    if (
      activeTab === "registrar1_payment" ||
      activeTab === "registrar1_assessment" ||
      activeTab === "registrar2"
    ) {
      extraColumns.push(
        columnHelper.accessor("judicial_division", {
          header: "Judicial Division",
          cell: (info) => (
            <span className="text-[#464255]">{info.getValue()}</span>
          ),
        }),
      );
    }

    if (activeTab === "president") {
      extraColumns.push(
        columnHelper.accessor("judicial_division", {
          header: "Judicial Division",
          cell: (info) => (
            <span className="text-[#464255]">{info.getValue()}</span>
          ),
        }),
        columnHelper.accessor("court_number", {
          header: "Court number",
          cell: (info) => (
            <span className="text-[#464255]">{info.getValue()}</span>
          ),
        }),
      );
    }

    return [
      ...baseColumns,
      ...extraColumns,
      columnHelper.display({
        id: "actions",
        header: "Action",
        cell: (info) => (
          <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button className="flex items-center text-gray-400 hover:text-gray-600 focus:outline-none">
                <MoreVertical className="w-5 h-5" />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                anchor="bottom end"
                className="absolute right-0 mt-2 w-52 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2"
              >
                <div className="px-1 py-1 flex flex-col gap-y-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active
                            ? "bg-[#E6F7F1] text-[#027A48]"
                            : "text-[#111827]"
                        } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                        onClick={() =>
                          navigate("/admin/users/view", {
                            state: {
                              user: info.row.original,
                              tab: activeTab,
                            },
                          })
                        }
                      >
                        View user
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={`${
                          active
                            ? "bg-gray-50 text-[#111827]"
                            : "text-[#111827]"
                        } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                      >
                        Suspend user
                      </button>
                    )}
                  </Menu.Item>
                  {(activeTab === "president" || activeTab === "lawyers") && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                        >
                          View case folder
                        </button>
                      )}
                    </Menu.Item>
                  )}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        ),
      }),
    ];
  }, [activeTab]);

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white">
        <div className="px-8 pt-6 pb-4 border-b w-full border-[#E9EAEB]">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">
            User categories
          </h1>
        </div>
        <div className="px-8 py-3 border-b border-[#E9EAEB] flex items-center justify-between">
          <div className="flex items-center gap-x-2 text-sm">
            <Link
              to="/admin/overview"
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Home
            </Link>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <span className="font-medium text-[#1A1A1A]">User categories</span>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-[#E9EAEB] px-8 pt-4">
        <div className="overflow-x-auto">
          <div className="flex items-center gap-6 text-sm font-medium whitespace-nowrap w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-[#111827] text-[#111827]"
                    : "border-transparent text-[#667085]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-8 py-6 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="relative w-full max-w-[320px]">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] bg-white text-sm placeholder:text-[#98A2B3]"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#98A2B3]" />
          </div>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#344054]">
              <span>{statusFilter}</span>
              <ChevronDown className="w-4 h-4 text-[#667085]" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 mt-2 w-40 origin-top-left rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                <div className="px-1 py-1 flex flex-col gap-y-1">
                  {["Status", "Active", "Inactive"].map((option) => (
                    <Menu.Item key={option}>
                      {({ active }) => (
                        <button
                          onClick={() => setStatusFilter(option)}
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                        >
                          {option}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>

          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#344054]">
              <span>{enabledFilter}</span>
              <ChevronDown className="w-4 h-4 text-[#667085]" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 mt-2 w-40 origin-top-left rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                <div className="px-1 py-1 flex flex-col gap-y-1">
                  {["Enabled", "Yes", "No"].map((option) => (
                    <Menu.Item key={option}>
                      {({ active }) => (
                        <button
                          onClick={() => setEnabledFilter(option)}
                          className={`${
                            active
                              ? "bg-gray-50 text-[#111827]"
                              : "text-[#111827]"
                          } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                        >
                          {option}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      <div className="flex-1 px-8 pb-10">
        <div className="bg-white rounded-2xl">
          <TableComponent
            DATA={mockData}
            // @ts-ignore
            COLUMNS={columns}
            sorting={sorting}
            setSorting={setSorting}
          />

          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E4E7EC] text-sm text-[#667085]">
            <div className="flex items-center gap-2">
              <span>
                Page <span className="font-medium">1</span> of{" "}
                <span className="font-medium">10</span>
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-lg border border-[#D0D5DD] bg-white hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-1.5 rounded-lg border border-[#D0D5DD] bg-white hover:bg-gray-50">
                  Next
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span>Entries per page:</span>
                <select className="border border-[#D0D5DD] rounded-lg px-2 py-1 bg-white focus:outline-none text-sm">
                  <option>10</option>
                  <option>20</option>
                  <option>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
